<template>
  <div
    v-if="fieldMetaData"
    class="general-view-tab row fullwidth"
  >
    <div class="small-8 columns general-view-tab-left">
      <TextField
        id="modules-tenders-view-general-projectDescription"
        :metaData="fieldMetaData.projectDescription"
        :readonly="true"
        :required="true"
      />
      <TextField
        id="modules-tenders-view-general-projectLocation"
        :metaData="fieldMetaData.projectLocation"
        :readonly="true"
        :required="true"
      />
      <TextField
        id="modules-tenders-view-general-projectStart"
        :metaData="fieldMetaData.projectStart"
        :readonly="true"
        :required="true"
      />
      <TextField
        id="modules-tenders-view-general-estimatedProjectDuration"
        :metaData="fieldMetaData.estimatedProjectDuration"
        :readonly="true"
        :required="true"
      />

      <div
        v-if="shouldShowClosingDate || shouldShowClosingDateExtended"
        class="row closing-date-extension align-bottom"
      >
        <TextField
          v-if="shouldShowClosingDate"
          id="modules-tenders-view-general-closingDate"
          class="small-4 medium-4 large-4"
          :metaData="fieldMetaData.closingDate"
          :readonly="true"
        />

        <TextField
          v-if="shouldShowClosingDateExtended"
          id="modules-tenders-view-general-closingDateExtended"
          :metaData="fieldMetaData.closingDateExtended"
          :readonly="true"
          :hideLabel="true"
        />
      </div>

      <working-hours
        id="modules-tenders-view-general-workingHours"
        :metaData="fieldMetaData.workingHours"
        :readonly="true"
      />
      <working-hours
        id="modules-tenders-view-general-noisyWorkingHours"
        :metaData="fieldMetaData.noisyWorkingHours"
        :readonly="true"
      />
      <TextField
        id="modules-tenders-edit-general-specialWorkingHours"
        :metaData="fieldMetaData.specialWorkingHours"
        :readonly="true"
        :required="false"
      />
      <TextField
        id="modules-tenders-edit-general-paymentTerms"
        :metaData="fieldMetaData.paymentTerms"
        :readonly="true"
        :required="false"
      />
      <TextField
        id="modules-tenders-view-general-validityPeriod"
        :metaData="fieldMetaData.validityPeriod"
        :readonly="true"
      />

      <TextField
        id="modules-tenders-view-general-onsiteVisit"
        :metaData="fieldMetaData.onsiteVisitRequired"
        :readonly="true"
      />

      <label
        v-if="shouldShowAdditionalItems"
        class="optional-label"
      >
        {{ $t('tender.general.optionalItems') }}:
      </label>
      <ul class="additionalItems-list">
        <template v-for="(option, index) in optionalItems">
          <li
            v-if="option.selected"
            :key="option.name"
            class="row"
            :class="{ [addendumClassName]: isOptionalItemRevised(index) }"
          >
            <div class="ssmall-4 medium-4 large-4">
              {{ option.name }}
            </div>
            <div class="ssmall-7 medium-7 large-7">
              {{ formatDecimal(option.value) }} {{ option.text }}
            </div>
          </li>
        </template>
      </ul>
      <div
        v-if="general.conditionsOfContracts"
        class="text-field coc-items"
        :class="{[addendumClassName]: isConditionsOfContractsRevised}"
      >
        <label>{{ $t('tender.general.conditionsOfContracts.label') }}:</label>
        <template v-if="cocI18nValue">
          <pre v-html="conditionOfContractsHtml" />
        </template>
      </div>
    </div>

    <div class="small-4 columns general-view-tab-right">
      <div v-if="!isContractor">
        <TextField
          id="modules-tenders-view-general-prNumber"
          :metaData="fieldMetaData.prNumber"
          :readonly="true"
        />
        <div class="text-field">
          <label class="budget-label">{{ $t('tender.general.estimatedBudget') }}: </label>
          <pre><p>{{ budget | formatCurrency({currencyCode}) }}</p></pre>
        </div>
      </div>

      <TextField
        v-show="!isContractor"
        id="modules-tenders-view-general-creator"
        :metaData="fieldMetaData.creator"
        :readonly="true"
      />
      <TextField
        id="modules-tenders-view-general-manager"
        :metaData="fieldMetaData.manager"
        :readonly="true"
      />
      <div
        v-if="isContractor"
        class="text-field"
      >
        <label class="site-label">{{ $t('general.phone') }}: </label>
        <pre><p>{{ picPhone || '--' }}</p></pre>

        <label class="site-label">{{ $t('general.email') }}: </label>
        <pre><p>{{ picEmail || '--' }}</p></pre>
      </div>

      <div
        v-show="!isContractor"
        v-if="showApprover"
      >
        <TextField
          id="modules-tenders-view-general-approver"
          :metaData="fieldMetaData.approver"
          :readonly="true"
        />
      </div>

      <div class="text-field">
        <label class="site-label">{{ $t('tender.general.siteId') }}: </label>
        <pre><p>{{ site }}</p></pre>
      </div>

      <div
        v-show="isContractor && deep(fieldMetaData,'onsiteVisitRequired.value')=== $i18n.t('yes')"
        class="onsite-info"
      >
        <label class="label-bold location-label">{{ $t('tender.general.onSiteLocation') }}</label>
        <pre>{{ deep(fieldMetaData, 'onsiteLocation.value') }}</pre>

        <label class="label-bold">{{ $t('tender.general.onSiteContact') }}</label>
        <pre>{{ deep(fieldMetaData, 'onsiteContact.value.name') }}</pre>
        <pre>{{ deep(fieldMetaData, 'onsiteContact.value.email') }}</pre>
        <pre>{{ deep(fieldMetaData, 'onsiteContact.value.phone') }}</pre>

        <label class="label-bold">{{ $t('vendor.onSiteVisitDateTime') }}</label>
        <pre>{{ visitDate }}</pre>
      </div>

      <div v-if="bids && !isContractor">
        <label
          class="vendor-label"
          for="tender.general.invitedVendors"
        >
          {{ $t('tender.general.vendors') }} ({{ (bids) ? bids.length : '-' }}):
        </label>
        <ul class="vendor-list">
          <li
            v-for="bid in bids"
            :key="bid.id"
          >
            <HoverTooltip
              :content="bid.company.name || ''"
              :url="hoverTooltipMail(bid.vendorId)"
            >
              <template
                v-if="invitedVendorDetails[bid.vendorId]"
              >
                <div>
                  {{ invitedVendorDetails[bid.vendorId]['contactAdmin'] }}
                </div>
                <div>
                  <a
                    :href="hoverTooltipMail(bid.vendorId)"
                    target="_top"
                  >
                    {{ invitedVendorDetails[bid.vendorId]['username'] }}
                  </a>
                </div>
                <div>
                  {{ invitedVendorDetails[bid.vendorId]['contactPhone'] }}
                </div>
                <div>
                  {{ hoverTooltipHintWord(bid.vendorId) }}
                </div>
              </template>
            </HoverTooltip>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import lodash from 'lodash'
import { mapGetters, mapState } from 'vuex'
import TextField from '@/modules/tenders/TextField'
import WorkingHours from '@/modules/tenders/WorkingHours'
import termsOfConditionsMixin from '@/modules/tenders/termsOfConditionsMixin'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'
import { contactTypes } from '@/api/users/consts'
import { dateFormat } from '@/utils/regex'
import { getBidById } from '@/api/bids/request'
import HoverTooltip from '@/controls/HoverTooltip'
import { getVendorDetailByIdAndSite } from '@/api/users/request'

export default {
  name: 'ViewGeneral',
  components: {
    TextField,
    WorkingHours,
    HoverTooltip,
  },
  mixins: [termsOfConditionsMixin],
  props: {
    isContractor: {
      type: Boolean,
      default: false,
    },
    tenderBidId: {
      type: String,
    },
  },
  data () {
    return {
      deep,
      visitDate: '',
      picEmail: '',
      picPhone: '',
      invitedVendorDetails: {},
    }
  },

  computed: {
    ...mapGetters('tenders', ['isOldTerm']),
    ...mapState('tenders', {
      general: state => state.currentTender.general,
      addendumClassName: state => state.addendumClassName,
      revisionHistory: state => state.recentRevisionHistory,
      fieldMetaData (state) {
        const currentTender = state.currentTender
        return this.initFieldMetaData(currentTender.general,
          this.revisionHistory, this.addendumClassName)
      },
      budget (state) {
        const currentTender = state.currentTender || {}
        const budget = (currentTender.purchaseRequisition || {}).budget
        return budget
      },
      currencyCode (state) {
        return lodash.get(state, 'currentTender.purchaseRequisition.currencyCode')
      },
      bids (state) {
        const currentTender = state.currentTender || {}
        return currentTender.bids
      },
      site (state) {
        const currentTender = state.currentTender || {}
        return (currentTender.purchaseRequisition || {}).siteId || currentTender.general.site
      },
    }),
    showApprover () {
      if (!this.fieldMetaData) {
        return false
      }
      return true
    },
    optionalItems () {
      if (!(this.fieldMetaData && this.fieldMetaData.optionalItems)) return null
      return this.fieldMetaData.optionalItems.value
    },
    shouldShowAdditionalItems () {
      if (!this.optionalItems) return false
      const checkedOptions = this.optionalItems
        .filter(item => item.selected)
      return checkedOptions.length > 0
    },
    shouldShowClosingDate () {
      if (!this.fieldMetaData) return false
      return (this.fieldMetaData.closingDate || {}).value
    },
    shouldShowClosingDateExtended () {
      if (!this.fieldMetaData) return false
      return (this.fieldMetaData.closingDateExtended || {}).value
    },
    isConditionsOfContractsRevised () {
      return this.revisionHistory && this.revisionHistory.conditionsOfContracts
    },
  },
  watch: {
    tenderBidId () {
      this.initTenderBid()
    },
  },
  async mounted () {
    if (this.tenderBidId) {
      await this.initTenderBid()
    }
    this.requestVendorDetail()
    this.$forceUpdate()
  },
  methods: {
    formatDecimal (amount) {
      const endWithDot = /[\d]+\.$/
      if (typeof amount === 'string' && endWithDot.test(amount)) {
        return amount.replace(/\.$/, '')
      }
      return amount
    },
    formatClosingDate (closingDate) {
      if (!closingDate || isNaN(dateUtil.toMoment(closingDate))) return ''
      const closingDateTime = dateUtil.convertDateToArray(dateUtil.toMoment(closingDate), dateFormat, '-')
      return `${closingDateTime[0]} ${closingDateTime[1]}`
    },
    initFieldsIfNotExist (metaData, general) {
      deep.p = true
      deep(metaData, 'creator.value', userHelper.getUserName(deep(general, 'creator.username') || '', deep(general, 'creator.displayName')))
      deep(metaData, 'manager.value', userHelper.getUserName(deep(general, 'manager.username') || '', deep(general, 'manager.displayName')))
      deep(metaData, 'manager.contacts', userHelper.getUserName(deep(general, 'manager.contacts') || '', deep(general, 'manager.contacts')))
      deep(metaData, 'approver.value', userHelper.getUserName(deep(general, 'approver.username') || '', deep(general, 'approver.displayName')))
      deep(metaData, 'validityPeriod.value', general.validityPeriod)
      deep(metaData, 'projectStart.value', general.projectStart)
      deep(metaData, 'onsiteVisitRequired.value', general.onsiteVisitRequired)
      deep(metaData, 'closingDate.value', general.closingDate)
      deep(metaData, 'closingDateExtended.value', general.closingDateExtended)
    },
    initFieldMetaData (general = {}, revisionHistory = {}, addendumClassName = '') {
      if (Object.keys(general).length <= 0) {
        return {}
      }
      const metaData = Object.keys(general)
        .reduce((partialData, fieldName) => ({
          ...partialData,
          [fieldName]: {
            fieldName,
            value: general[fieldName],
            isRevised: !!revisionHistory[fieldName] && general.version > 0,
            addendumClassName,
          },
        }), {})

      this.initFieldsIfNotExist(metaData, general)
      const label = this.$i18n.t('tender.general.validityPeriodLabel')
      const validityPeriodStr = `${general.validityPeriod} ${label}`
      metaData.validityPeriod.value = validityPeriodStr

      const projectStartStr = `${general.projectStart}${this.projectStartLabel}`
      metaData.projectStart.value = projectStartStr

      const onsiteVisitRequired = metaData.onsiteVisitRequired.value
      metaData.onsiteVisitRequired.value = this.$i18n.t(onsiteVisitRequired ? 'yes' : 'no')
      const closingDate = metaData.closingDate.value
      metaData.closingDate.value = this.formatClosingDate(closingDate)

      if (Array.isArray(metaData.manager.contacts)) {
        let picEmail = ''
        let picPhone = ''
        metaData.manager.contacts.forEach(item => {
          if (item.priority && item.type === contactTypes.EMAIL) {
            picEmail = item.value
          } else if (item.priority && item.type === contactTypes.PHONE_NUMBER) {
            picPhone = item.value
          }
        })
        this.picEmail = picEmail
        this.picPhone = picPhone
      }

      if (metaData.closingDateExtended.value === true) {
        metaData.closingDateExtended.value = this.$t('tender.extensionRequested')
      }
      return metaData
    },
    async initTenderBid () {
      if (!(this.isContractor && this.tenderBidId)) {
        return
      }
      const response = await getBidById(this.tenderBidId)

      if (response && response.bid && response.bid.visitDate) {
        this.visitDate = moment(response.bid.visitDate).format('DD MMM YY, HH:mm')
      }
    },
    isOptionalItemRevised (index) {
      if (!this.revisionHistory) return false

      const revisedItems = this.revisionHistory.optionalItems
      return (revisedItems === undefined) ? false : revisedItems[index] !== undefined
    },
    async requestVendorDetail () {
      const list = await Promise.all(
        this.bids.map(async (bid) => {
          const [venderDetail] = await getVendorDetailByIdAndSite(bid.vendorId, this.general.site, true)
          return venderDetail
        }),
      )

      this.invitedVendorDetails = list.reduce((obj, current) => {
        return {...obj, [current.vendorId]: current}
      }, {})
    },
    hoverTooltipMail (vendorId) {
      return this.invitedVendorDetails[vendorId] ? `mailto:${this.invitedVendorDetails[vendorId].username}` : ''
    },
    hoverTooltipHintWord (vendorId) {
      return !this.invitedVendorDetails[vendorId].enabled ||
            !this.invitedVendorDetails[vendorId].invitedAt ? this.$t('hint.venderInfoHint') : ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/tender/addendum';

.additionalItems-list {
  list-style-type: none;
  margin-left: 0;
  color: #0C3A58;
}

.term-header {
  margin-bottom: 0.65rem;
}

.term-box {
  display: flex;
}

.term {
  padding-left: 10px;
  margin-bottom: 0px;
}

.closing-date {
  margin-left: 15px;
  color: #0C3A58;
  font-family: Verdana;
  font-size: 14px;
  line-height: 1.43;
  text-align: left;
}
.onsite-label {
  width: auto;
  display: inline-block;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0 0;
}
.onsite-value {
  width: 35%;
  display: inline-block;
}
.vendor-list {
  list-style-type: decimal;
  list-style-position: outside;
  margin: 0 0 5px 0;
  padding-left: 20px;
  color: #0C3A58;
}
.closing-date-extension {
  margin: 0;
  .text-field {
    margin: 0 15px 0 0;
  }
}
.closing-date-extension-label {
  margin: 45px 10px 0 0;
  color: #e69500;
}

.general-view-tab .working-hours{
  & label{
    margin-bottom: 0;
  }
}
.general-view-tab .text-field{
  margin: 0;
}
.general-view-tab-left {
  padding-right: 20px;
  &>div {
    border-bottom: 1px dashed #e0e0e0;
  }
  .working-hours {
    border-bottom: none;
  }
}
.general-view-tab-right {
  padding-left: 24px;
  background: white;
  .text-field {
    label {
      margin: 5px 0 0 0;
      line-height: 2;
    }
    pre>p {
      margin-bottom: 5px;
      line-height: 2;
    }
  }
}
.budget-label {
  margin-top: 5px;
  margin-bottom: 0;
  line-height: 2;
}

.vendor-label {
  margin-bottom: 2px;
  margin-top: 0;
  line-height: 2;
}
.optional-label {
  margin: 5px 0 0 0;
  line-height: 2;
}
.location-label {
  margin-top: 1px;
}
.onsite-info {
  margin-bottom: 15px;
}
.coc-items ::v-deep .flex{
  display: flex;
}
.coc-items ::v-deep p {
  margin-bottom: 0;
}
</style>
<style lang="scss">
.general-view-tab-left {
  .working-hours {
    &>div {
      border-bottom: 1px dashed #e0e0e0;
    }
  }
}
</style>
